import type { LayerSpecification } from "maplibre-gl";
import type { ComponentId } from "./types";

export const COMPONENT_ORDER: ComponentId[] = [
  "land",
  "water",
  "landuse",
  "roads",
  "buildings",
  "borders",
  "labels",
  "pois",
  "other",
];

export type Classification = { component: ComponentId; section: string };

const SYMBOL_SECTIONS: { [sourceLayer: string]: Classification } = {
  places: { component: "labels", section: "places" },
  roads: { component: "labels", section: "road_names" },
  water: { component: "labels", section: "water_names" },
  pois: { component: "pois", section: "pois" },
};

const SHAPE_SECTIONS: { [sourceLayer: string]: Classification } = {
  earth: { component: "land", section: "land" },
  landcover: { component: "land", section: "landcover" },
  water: { component: "water", section: "water" },
  landuse: { component: "landuse", section: "landuse" },
  buildings: { component: "buildings", section: "buildings" },
  boundaries: { component: "borders", section: "borders" },
};

function roadSection(id: string): string {
  if (/tunnel/.test(id)) return "tunnels";
  if (/rail|runway|taxiway|pier/.test(id)) return "rail_airport";
  if (/casing/.test(id)) return "outlines";
  return "surface";
}

// Sorts a layer into a component by its source-layer and type. The layer id
// only refines the section inside Roads, so styles with unfamiliar ids still
// land in the right component.
export function classifyLayer(layer: LayerSpecification): Classification {
  if (layer.type === "background") {
    return { component: "land", section: "background" };
  }
  const sourceLayer: string =
    (layer as { "source-layer"?: string })["source-layer"] || "";

  if (layer.type === "symbol") {
    return (
      SYMBOL_SECTIONS[sourceLayer] || {
        component: "other",
        section: sourceLayer || layer.id,
      }
    );
  }
  if (sourceLayer === "roads") {
    return { component: "roads", section: roadSection(layer.id) };
  }
  return (
    SHAPE_SECTIONS[sourceLayer] || {
      component: "other",
      section: sourceLayer || layer.id,
    }
  );
}

const ID_PREFIX =
  /^(roads|landuse|places|water|boundaries|pois|landcover|earth|buildings)_/;
const ID_NOISE = /^(casing|early|late|tunnels|bridges|labels|label)$/;

export function titleize(text: string): string {
  const spaced = text.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// "roads_bridges_highway_casing" -> "Highway"
export function layerName(id: string): string {
  const words = id
    .replace(ID_PREFIX, "")
    .split("_")
    .filter((word) => word && !ID_NOISE.test(word));
  return titleize(words.length > 0 ? words.join(" ") : id);
}
